import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Users,
  ClipboardList,
  ChevronRight,
  Gauge,
  LayoutGrid,
  CalendarRange,
  CalendarClock,
  CalendarDays,
  Zap,
  UserPlus,
  Tags,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";

export const Route = createFileRoute("/admin/")({
  component: AdminPage,
});

function AdminPage() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Administración"
        subtitle="Gestión de equipo, plantillas de evaluación y configuración del panel."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AdminCard
          to="/admin/users"
          icon={<Users className="size-5" />}
          title="Usuarios"
          description="Listar equipo, asignar roles globales (admin/finanzas/lead/member) y sincronizar desde Google Workspace."
        />
        {/* "Evaluaciones (marcos)" pausada — ver EVALUATIONS_PAUSED.md.
        <AdminCard
          to="/admin/assessments"
          icon={<Layers className="size-5" />}
          title="Evaluaciones (marcos)"
          description="Marcos que combinan KPIs y cuestionarios con scoring para devolver un status. Lo que el equipo ve por defecto. Cambios versionados."
        /> */}
        <AdminCard
          to="/admin/kpi-definitions"
          icon={<Gauge className="size-5" />}
          title="KPIs (definiciones)"
          description="Inputs cuantitativos auto/manuales. Umbrales en 4 niveles (excelente/bien/mejorable/malo)."
        />
        <AdminCard
          to="/admin/questionnaires"
          icon={<ClipboardList className="size-5" />}
          title="Cuestionarios (plantillas)"
          description="Encuestas que responde la gente — inputs cuali/cuantitativos subjetivos. Anónimas, por nivel."
        />
        <AdminCard
          to="/admin/evaluations-config"
          icon={<LayoutGrid className="size-5" />}
          title="Config overview"
          description="Vista única con todas las plantillas y KPIs, toggles de activo/visibilidad."
        />
        <AdminCard
          to="/admin/calendar-events"
          icon={<CalendarRange className="size-5" />}
          title="Eventos del calendario"
          description="Feriados, cierres de empresa, recordatorios globales. Aparecen en el calendario de todo el equipo y los días marcados como 'no laboral' afectan el prefill de standups."
        />
        <AdminCard
          to="/admin/google-calendar-sync"
          icon={<CalendarClock className="size-5" />}
          title="Sync Google Calendar"
          description="Estado del mirror de Google Calendar por usuario. Corre cada 10 min. Útil para diagnosticar permisos del workspace o tokens expirados."
        />
        <AdminCard
          to="/admin/days-off"
          icon={<CalendarDays className="size-5" />}
          title="Días libres"
          description="Cola de solicitudes pendientes, histórico de aprobadas/rechazadas. Filtro por persona y stats del año."
        />
        <AdminCard
          to="/admin/clients"
          icon={<UserPlus className="size-5" />}
          title="Clientes externos"
          description="Invitar, editar y resetear contraseñas de usuarios del portal de clientes (clientes.axorial.mx). Los invites se generan como URL que el admin comparte manualmente — no se mandan emails."
        />
        <AdminCard
          to="/admin/project-categories"
          icon={<Tags className="size-5" />}
          title="Categorías de proyecto"
          description="Tipos configurables (web, ecommerce, cloud, mobile, backend, frontend, database, etc). Se asignan many-to-many a cada proyecto y se muestran como badges en el portal del cliente."
        />
        <AdminCard
          to="/admin/sync"
          icon={<Zap className="size-5" />}
          title="Sincronización manual"
          description="Dispara los jobs que normalmente corren cada 30 min: Google Calendar, Plane (milestones + cache), replies de standups en MM. Útil cuando necesitas resultados inmediatos."
        />
      </div>
    </div>
  );
}

function AdminCard({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="card-elevated p-5 flex items-start gap-4 hover:border-primary/40 transition-colors group"
    >
      <div className="size-10 rounded-lg bg-primary/15 text-primary grid place-items-center shrink-0">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold flex items-center gap-2">
          {title}
          <ChevronRight className="size-4 text-base-content/30 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
        </div>
        <div className="text-sm text-base-content/65 mt-1 leading-relaxed">{description}</div>
      </div>
    </Link>
  );
}
